/**
 * Task 35: Unified Candidate Form
 * Works across Desktop + Mobile + PWA
 * Features: Validation, Duplicate Detection, EventBus Integration, Resume Upload
 */

import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, MapPin, Briefcase, IndianRupee, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import axiosInstance from '../../config/axiosConfig';
import eventBus from '../../utils/eventBus';
import toast from '../../utils/toast';
import { formatCandidateApiError } from '../../utils/apiError';

const buildInitialFormData = (prefilledJdId, initialCandidate = null) => ({
  name: initialCandidate?.name || '',
  phone: initialCandidate?.phone || '',
  gender: '',
  current_city: initialCandidate?.location || '',
  jd_id: prefilledJdId || '',
  email: initialCandidate?.email || '',
  source: '',
  role: initialCandidate?.role || '', // New candidate default remains role: ''
  experience: initialCandidate?.parsed_experience_years != null ? String(initialCandidate.parsed_experience_years) : '',
  skills: initialCandidate?.parsed_skills_text || '',
  current_company: '',
  expected_salary: '',
  notes: '',
  status: initialCandidate?.status || 'Active'
});

const CandidateForm = ({ 
  onSuccess, 
  onCancel, 
  prefilledJdId = null,
  showCallImmediately = false, // Mobile only feature
  source = 'CandidateForm',
  mode = 'create',
  initialCandidate = null
}) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState(() => buildInitialFormData(prefilledJdId, isEditMode ? initialCandidate : null));

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [jobDescriptions, setJobDescriptions] = useState([]);

  // Load JDs for dropdown
  useEffect(() => {
    if (!isEditMode) {
      fetchJobDescriptions();
    }
  }, [isEditMode]);

  useEffect(() => {
    setFormData(buildInitialFormData(prefilledJdId, isEditMode ? initialCandidate : null));
    setErrors({});
    setDuplicateCheck(null);
  }, [prefilledJdId, initialCandidate, isEditMode]);

  const fetchJobDescriptions = async () => {
    try {
      const response = await axiosInstance.get('/jds/list?status_filter=Active');
      setJobDescriptions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch JDs:', error);
    }
  };

  // Real-time duplicate detection on phone change
  useEffect(() => {
    if (isEditMode) {
      setDuplicateCheck(null);
      return;
    }

    const checkDuplicate = async () => {
      if (formData.phone && formData.phone.replace(/\D/g, '').length === 10) {
        try {
          const response = await axiosInstance.get('/candidates/check-duplicate', {
            params: { phone: formData.phone }
          });
          if (response.data.exists) {
            setDuplicateCheck(response.data.candidate);
          } else {
            setDuplicateCheck(null);
          }
        } catch (error) {
          console.error('Duplicate check failed:', error);
        }
      } else {
        setDuplicateCheck(null);
      }
    };

    const debounceTimeout = setTimeout(checkDuplicate, 500);
    return () => clearTimeout(debounceTimeout);
  }, [formData.phone, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const phoneDigits = formData.phone.replace(/\D/g, '');
    const emailValue = formData.email.trim();
    if (!isEditMode && !phoneDigits) {
      newErrors.phone = 'Phone number is required.';
    } else if (phoneDigits && phoneDigits.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits.';
    }

    if (!isEditMode && !emailValue) {
      newErrors.email = 'Email is required.';
    } else if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (isEditMode && !phoneDigits && !emailValue) {
      newErrors.phone = 'Enter a phone number or email address.';
      newErrors.email = 'Enter a phone number or email address.';
    }

    const skillTokens = formData.skills.split(/[,;|\n]+/).filter(skill => skill.trim());
    if (!isEditMode && skillTokens.length === 0) {
      newErrors.skills = 'At least one skill is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditMode && duplicateCheck) {
      toast.error('Candidate already exists! Please check the details.');
      return;
    }

    if (!validateForm()) {
      toast.error('Please complete the required candidate fields.');
      return;
    }

    setLoading(true);

    try {
      const response = isEditMode
        ? await axiosInstance.patch(`/candidates/${initialCandidate.id}`, {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            skills: formData.skills,
            experience: formData.experience,
            role: formData.role,
            current_city: formData.current_city,
            status: formData.status
          })
        : await axiosInstance.post('/candidates', {
            ...formData,
            jd_id: formData.jd_id || null,
            source: formData.source || source
          });

      const newCandidate = response.data;

      if (!isEditMode) try {
        eventBus.emit('candidate_added', {
          recruiter_id: null, // Backend auto-assigns
          candidate_id: newCandidate.id,
          jd_id: formData.jd_id || null,
          channel: isMobileDevice() ? 'mobile' : 'desktop',
          source: source,
          timestamp: new Date().toISOString(),
          metadata: {
            candidate_name: newCandidate.name,
            candidate_phone: newCandidate.phone,
            has_email: !!formData.email,
            gender: formData.gender,
            current_city: formData.current_city
          }
        });
      } catch (eventBusError) {
        // R10: EventBus failure warning - don't block candidate creation
        console.warn('EventBus failed to emit candidate_added event:', eventBusError);
        toast.warning('Analytics tracking failed, but candidate was created successfully');
      }

      if (isEditMode) {
        toast.success('Candidate updated successfully!');
      } else {

      toast.success('Candidate added successfully! 🎉');

      }

      if (onSuccess) {
        onSuccess(newCandidate);
      }

      if (!isEditMode) {
        // Reset restores the same role: '' default used for a fresh candidate.
        setFormData(buildInitialFormData(prefilledJdId));
      }

    } catch (error) {
      console.error(isEditMode ? 'Failed to update candidate:' : 'Failed to create candidate:', error);
      const formatted = formatCandidateApiError(
        error.response?.data?.detail,
        isEditMode ? 'Failed to update candidate.' : 'Failed to add candidate.'
      );
      if (Object.keys(formatted.fieldErrors).length > 0) {
        setErrors(previous => ({ ...previous, ...formatted.fieldErrors }));
      }
      toast.error(formatted.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto card-light">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Edit Candidate' : 'Add New Candidate'}</h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        )}
      </div>

      {/* Duplicate Warning */}
      {duplicateCheck && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-yellow-800">Candidate Already Exists!</p>
            <p className="text-sm text-yellow-700 mt-1">
              {duplicateCheck.masked
                ? `${duplicateCheck.candidate_reference || duplicateCheck.name} belongs to ${duplicateCheck.primary_owner?.name || 'another recruiter pipeline'}. Contact your TL or manager if allocation is required.`
                : `${duplicateCheck.name}${duplicateCheck.phone ? ` (${duplicateCheck.phone})` : ''}${duplicateCheck.id ? ` - ID: ${duplicateCheck.id}` : ''}`}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Mandatory Section */}
        <div className="border-b pb-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Mandatory Information</h3>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full name"
                required
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'candidate-name-error' : undefined}
              />
            </div>
            {errors.name && <p id="candidate-name-error" className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="10-digit mobile number"
                maxLength={10}
                required={!isEditMode}
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'candidate-phone-error' : undefined}
              />
            </div>
            {errors.phone && <p id="candidate-phone-error" className="text-xs text-red-500 mt-1">{errors.phone}</p>}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="candidate@example.com"
                required={!isEditMode}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'candidate-email-error' : undefined}
              />
            </div>
            {errors.email && <p id="candidate-email-error" className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          {/* Skills */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Skills {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData.skills}
              onChange={(e) => handleInputChange('skills', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 ${
                errors.skills ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., React, Node.js, Python"
              required={!isEditMode}
              aria-invalid={!!errors.skills}
              aria-describedby={errors.skills ? 'candidate-skills-error' : undefined}
            />
            {errors.skills && <p id="candidate-skills-error" className="text-xs text-red-500 mt-1">{errors.skills}</p>}
          </div>
        </div>

        {/* Optional Section */}
        <div className="border-b pb-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Optional Information</h3>

          {/* Gender */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender
            </label>
            <select
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
            >
              <option value="" className="text-gray-500">Not specified</option>
              <option value="Male" className="text-gray-900">Male</option>
              <option value="Female" className="text-gray-900">Female</option>
              <option value="Other" className="text-gray-900">Other</option>
            </select>
          </div>}

          {/* Current City */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEditMode ? 'Location' : 'Current City / Location'}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.current_city}
                onChange={(e) => handleInputChange('current_city', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
                placeholder="e.g., Mumbai, Pune"
              />
            </div>
          </div>

          {/* JD Mapping */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description
            </label>
            <select
              value={formData.jd_id}
              onChange={(e) => handleInputChange('jd_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
            >
              <option value="" className="text-gray-500">No JD mapping</option>
              {jobDescriptions.map(jd => (
                <option key={jd.id} value={jd.id} className="text-gray-900">
                  {jd.role_title} - {jd.client_name}
                </option>
              ))}
            </select>
          </div>}

          {/* Source */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Candidate Source
            </label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => handleInputChange('source', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
              placeholder="e.g., Referral, LinkedIn, Naukri"
            />
          </div>}

          {/* Current Role */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current / Most Recent Role
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
                placeholder="e.g., Business Analyst"
              />
            </div>
          </div>

          {/* Experience */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Experience
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.experience}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 3-5 years"
              />
            </div>
          </div>

          {isEditMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="Active" className="text-gray-900">Active</option>
                <option value="Inactive" className="text-gray-900">Inactive</option>
              </select>
            </div>
          )}

          {/* Current Company */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Company
            </label>
            <input
              type="text"
              value={formData.current_company}
              onChange={(e) => handleInputChange('current_company', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
              placeholder="Current employer"
            />
          </div>}

          {/* Expected Salary */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expected Salary
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={formData.expected_salary}
                onChange={(e) => handleInputChange('expected_salary', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 5-7 LPA"
              />
            </div>
          </div>}

          {/* Notes */}
          {!isEditMode && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 text-gray-400" size={20} />
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
                placeholder="Additional notes about the candidate"
                rows={3}
              />
            </div>
          </div>}
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={loading || (!isEditMode && !!duplicateCheck)}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Saving...' : 'Adding...'}
              </>
            ) : (
              <>
                <CheckCircle className="mr-2" size={20} />
                {isEditMode ? 'Save Changes' : 'Add Candidate'}
              </>
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CandidateForm;
